import { CbhFeatureFlag, FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { addHours } from "date-fns";
import { useCallback, useMemo } from "react";

const MIN_HOURS_BETWEEN_SHOWS = 8;
const MAX_HOURS_BETWEEN_SHOWS = 24;

const FORTUNE_COOKIE_NEXT_SHOW_TIME_KEY = "fortune-cookie-next-show-time";
const FORTUNE_COOKIE_IS_CURRENTLY_SHOWING_KEY = "fortune-cookie-is-currently-showing";

export function useShouldShowFortuneCookie() {
  const fortuneCookieConfig = useCbhFlag(CbhFeatureFlag.FORTUNE_COOKIE_ANIMATION, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.FORTUNE_COOKIE_ANIMATION],
  });

  const [nextShowTime, setNextShowTime] = useLocalStorage<number | undefined>(
    FORTUNE_COOKIE_NEXT_SHOW_TIME_KEY,
    undefined
  );

  // Track if fortune cookie is currently visible in this session
  // Once it's shown, keep it visible until the user interacts with it
  // Store in localStorage so all hook instances share the same state
  const [isCurrentlyShowing, setIsCurrentlyShowing] = useLocalStorage<boolean>(
    FORTUNE_COOKIE_IS_CURRENTLY_SHOWING_KEY,
    false
  );

  const shouldShow = useMemo(() => {
    // Always check the feature flag first to ensure we respect disabled state
    if (!fortuneCookieConfig.enabled) {
      return false;
    }

    if (!fortuneCookieConfig.fortuneMessages?.length) {
      return false;
    }

    // If already showing in this session, keep showing it
    if (isCurrentlyShowing) {
      return true;
    }

    return !nextShowTime || Math.floor(Date.now() / 1000) >= nextShowTime;
  }, [
    fortuneCookieConfig.enabled,
    fortuneCookieConfig.fortuneMessages?.length,
    nextShowTime,
    isCurrentlyShowing,
  ]);

  const fortuneMessage = useMemo(() => {
    if (!fortuneCookieConfig.fortuneMessages?.length) {
      return "";
    }

    const randomIndex = Math.floor(Math.random() * fortuneCookieConfig.fortuneMessages.length);
    return fortuneCookieConfig.fortuneMessages[randomIndex];
  }, [fortuneCookieConfig.fortuneMessages]);

  const markAsShowing = useCallback(() => {
    setIsCurrentlyShowing(true);
  }, [setIsCurrentlyShowing]);

  const recordShown = useCallback(() => {
    // Randomly select a time between 8 and 24 hours in advance
    const randomHours =
      MIN_HOURS_BETWEEN_SHOWS + Math.random() * (MAX_HOURS_BETWEEN_SHOWS - MIN_HOURS_BETWEEN_SHOWS);
    const nextTime = addHours(new Date(), randomHours);
    setNextShowTime(Math.floor(nextTime.getTime() / 1000));
  }, [setNextShowTime]);

  const hide = useCallback(() => {
    setIsCurrentlyShowing(false);
  }, [setIsCurrentlyShowing]);

  return {
    shouldShow,
    fortuneMessage,
    markAsShowing,
    recordShown,
    hide,
  };
}
